import {
    Box,
    Button,
    Collapse,
    Divider,
    Flex,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Icon } from '../../ui';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_TINT,
    SETTINGS_BORDER_COLOR,
} from '../../components/settings/primitives/constants';
import type { SettingsGroup, SettingsNavEntry } from './settingsNav';

export interface SettingsSidebarProps {
    groups: Array<{ group: SettingsGroup; entries: SettingsNavEntry[] }>;
    activeId: string | undefined;
    onSelect: (id: string) => void;
    onBack: () => void;
    /** Shown above the groups; hidden for users without full settings access. */
    onOverviewSelect?: () => void;
    isOverviewActive?: boolean;
    /** Search is rendered only when both are provided (full-access users). */
    searchQuery?: string;
    onSearchChange?: (value: string) => void;
}

const SettingsSidebar = ({
    groups,
    activeId,
    onSelect,
    onBack,
    onOverviewSelect,
    isOverviewActive = false,
    searchQuery,
    onSearchChange,
}: SettingsSidebarProps) => {
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    const toggleGroup = (groupId: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    return (
        <Flex
            direction="column"
            w="260px"
            flexShrink={0}
            bg="gray.50"
            borderRightWidth="1px"
            borderColor={SETTINGS_BORDER_COLOR}
            h="100%"
        >
            <Box flexShrink={0} pt={3} px={3}>
                <Button
                    variant="ghost"
                    size="sm"
                    fontWeight="medium"
                    color="gray.600"
                    px={2}
                    leftIcon={<Icon name="arrowLeft" size={4} />}
                    _hover={{ bg: 'gray.200', color: 'gray.800' }}
                    onClick={onBack}
                >
                    Back to app
                </Button>

                <Text fontSize="lg" fontWeight="semibold" color="gray.800" px={2} mt={3} mb={onSearchChange ? 3 : 4}>
                    Settings
                </Text>

                {onSearchChange && (
                    <InputGroup size="sm" mb={3}>
                        <InputLeftElement pointerEvents="none">
                            <Icon name="search" color="gray.400" size={4} />
                        </InputLeftElement>
                        <Input
                            value={searchQuery ?? ''}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search settings"
                            borderRadius="md"
                            bg="white"
                            borderColor={SETTINGS_BORDER_COLOR}
                            _focusVisible={{
                                borderColor: PARTBAY_BLUE,
                                boxShadow: `0 0 0 1px ${PARTBAY_BLUE}`,
                            }}
                        />
                    </InputGroup>
                )}
            </Box>

            <Box flex={1} minH={0} overflowY="auto" pb={5}>
                {onOverviewSelect && (
                    <Box px={3} mb={2}>
                        <Button
                            w="100%"
                            h="36px"
                            justifyContent="flex-start"
                            px={2}
                            borderRadius="md"
                            fontSize="sm"
                            fontWeight={isOverviewActive ? 'semibold' : 'medium'}
                            bg={isOverviewActive ? PARTBAY_BLUE_TINT : 'transparent'}
                            color={isOverviewActive ? PARTBAY_BLUE : 'gray.600'}
                            _hover={{ bg: PARTBAY_BLUE_TINT, color: PARTBAY_BLUE }}
                            onClick={onOverviewSelect}
                        >
                            Overview
                        </Button>
                    </Box>
                )}

                {groups.map(({ group, entries }, groupIndex) => {
                    const isCollapsed = collapsedGroups.has(group.id);
                    return (
                        <Box key={group.id}>
                            {(groupIndex > 0 || onOverviewSelect) && (
                                <Divider borderColor={SETTINGS_BORDER_COLOR} my={3} />
                            )}

                            <Flex
                                as="button"
                                type="button"
                                align="center"
                                justify="space-between"
                                w="100%"
                                px={5}
                                py={1}
                                mb={1}
                                onClick={() => toggleGroup(group.id)}
                                _hover={{ '& > p': { color: 'gray.700' } }}
                            >
                                <Text
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="gray.500"
                                    letterSpacing="wider"
                                    textTransform="uppercase"
                                >
                                    {group.label}
                                </Text>
                                <Icon
                                    name="chevronDown"
                                    size={3.5}
                                    color="gray.400"
                                    transform={isCollapsed ? 'rotate(-90deg)' : undefined}
                                    transition="transform 0.15s"
                                />
                            </Flex>

                            <Collapse in={!isCollapsed} animateOpacity>
                                <VStack spacing={0.5} align="stretch" px={3} pb={1}>
                                    {entries.map((entry) => {
                                        const isActive = entry.id === activeId;
                                        return (
                                            <Button
                                                key={entry.id}
                                                h="36px"
                                                justifyContent="flex-start"
                                                px={2}
                                                borderRadius="md"
                                                fontSize="sm"
                                                fontWeight={isActive ? 'semibold' : 'medium'}
                                                bg={isActive ? PARTBAY_BLUE_TINT : 'transparent'}
                                                color={isActive ? PARTBAY_BLUE : 'gray.600'}
                                                _hover={{ bg: PARTBAY_BLUE_TINT, color: PARTBAY_BLUE }}
                                                onClick={() => onSelect(entry.id)}
                                            >
                                                <HStack spacing={2.5} w="100%">
                                                    <Icon
                                                        name={entry.icon}
                                                        size={4}
                                                        color={isActive ? PARTBAY_BLUE : 'gray.400'}
                                                    />
                                                    <Text noOfLines={1}>{entry.label}</Text>
                                                </HStack>
                                            </Button>
                                        );
                                    })}
                                </VStack>
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>
        </Flex>
    );
};

export default SettingsSidebar;
